import {
  Avatar,
  Box,
  Button,
  Card,
  Stack,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useBasePath } from 'components/Providers'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

export default function VisitorLogList({ list, tabIndex }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const basePath = useBasePath()

  const calculateDuration = (date, start, end) => {
    if (!date || !start || !end) return ''

    const startValue = start.includes('T')
      ? start
      : `${date}T${start}`

    const endValue = end.includes('T')
      ? end
      : `${date}T${end}`

    const startTime = dayjs(startValue)
    const endTime = dayjs(endValue)

    if (!startTime.isValid() || !endTime.isValid()) return ''

    const minutes = endTime.diff(startTime, 'minute')

    if (minutes < 60) {
      return `${minutes} ${t('min')}`
    }

    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    return `${hours} ${t('hr')} ${remainingMinutes} ${t('min')}`
  }

  const getInitials = item => {
    const firstName = item.visitorFirstName || ''
    const lastName = item.visitorLastName || ''

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const getName = item => {
    return `${item.visitorFirstName || ''} ${
      item.visitorLastName || ''
    }`.trim()
  }

  const handleCheckout = item => {
    navigate(`${basePath}/${item.id}`, {
      state: {
        action: 'checkout',
      },
    })
  }

  const formatTime = (date, time) => {
    if (!time) return '-'

    const value = time.includes('T')
      ? time
      : `${date}T${time}`

    return dayjs(value).isValid()
      ? dayjs(value).format('hh:mm A')
      : '-'
  }

  if (!list?.length) {
    const messages = [
      t('No visitors currently in the building.'),
      t('No visitors have checked out today.'),
      t('No student pickups today.'),
    ]

    return (
      <Typography color="text.secondary">
        {messages[tabIndex]}
      </Typography>
    )
  }

  return (
    <Stack spacing={1.5}>
      {list.map(item => (
        <Card
          key={item.id}
          variant="outlined"
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: 1,
          }}
        >
          <Avatar sx={{ width: 48, height: 48 }}>
            {getInitials(item)}
          </Avatar>

          <Box sx={{ flexGrow: 1 }}>
            <Typography>
              {getName(item)}
            </Typography>

            {item.visitType !== 'pickup' && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  textTransform: 'capitalize',
                }}
              >
                {t('Visitor Type')}: {t(item.visitorType || '-')}
              </Typography>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
            >
              {item.note || (
                item.visitType === 'pickup'
                  ? t('Student Pickup')
                  : t('Visitor')
              )}
            </Typography>

            {item.visitType === 'pickup' &&
              item.visitedPerson && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    border: '1px solid',
                    borderColor: 'primary.light',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="primary"
                  >
                    {t('Student')}:{' '}
                    {item.visitedPerson.firstName}{' '}
                    {item.visitedPerson.lastName}
                  </Typography>

                  {item.relationship && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ display: 'block' }}
                    >
                      {t('Relationship')}:{' '}
                      {item.relationship}
                    </Typography>
                  )}
                </Box>
              )}

            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 1 }}
            >
              {item.checkInAt && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('In')}:{' '}
                  {formatTime(
                    item.visitDate,
                    item.checkInAt,
                  )}
                </Typography>
              )}

              {item.checkOutAt && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('Out')}:{' '}
                  {formatTime(
                    item.visitDate,
                    item.checkOutAt,
                  )}
                </Typography>
              )}

              {item.checkInAt && item.checkOutAt && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {calculateDuration(
                    item.visitDate,
                    item.checkInAt,
                    item.checkOutAt,
                  )}
                </Typography>
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: 'error.main',
              border: '2px solid',
              borderColor: 'grey.300',
            }}
          />

          {!item.checkOutAt &&
            item.visitType !== 'pickup' && (
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleCheckout(item)}
              >
                {t('Check Out')}
              </Button>
            )}
        </Card>
      ))}
    </Stack>
  )
}